import { toggleIdInList } from '@griply/utils';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useViewOptionsSectionCollapsed } from 'features/user';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PopupMenuCheckbox } from 'shared/components/ui/popup-menu';
import {
  SideNavigationPanelItem,
  SideNavigationPanelSection,
} from 'shared/components/ui/side-navigation-panel';
import { useUpcomingCalendarViewOptions } from 'shared/contexts/upcoming-calendar-view-options';
import { ID } from 'shared/types/id';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import { ViewOptionsSectionId } from 'shared/types/view-options-section';

export type LifeAreasSectionProps = {
  isFirst?: boolean;
};

export const LifeAreasSection: React.FC<LifeAreasSectionProps> = ({
  isFirst,
}) => {
  const { t } = useTranslation();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const { viewOptions, updateViewOptions } = useUpcomingCalendarViewOptions();
  const { hiddenLifeAreaIds } = viewOptions;

  const [collapsed, setCollapsed] = useViewOptionsSectionCollapsed(
    ViewOptionsSectionId.LifeAreas,
  );
  const onToggleSection = () => setCollapsed(!collapsed);

  const toggleLifeArea = useCallback(
    (lifeAreaId: ID) =>
      updateViewOptions({
        hiddenLifeAreaIds: toggleIdInList(hiddenLifeAreaIds, lifeAreaId),
      }),
    [hiddenLifeAreaIds, updateViewOptions],
  );

  const onToggleNoLifeArea = () => toggleLifeArea(lifeAreaOtherID);
  const showNoLifeArea = !hiddenLifeAreaIds.includes(lifeAreaOtherID);

  // The "No life area" row counts as one of the rows the section offers.
  const totalCount = lifeAreas.length + 1;
  const hiddenCount = lifeAreas.filter(({ id }) =>
    hiddenLifeAreaIds.includes(id),
  ).length;
  const enabledCount = totalCount - hiddenCount - (showNoLifeArea ? 0 : 1);

  return (
    <SideNavigationPanelSection
      id={ViewOptionsSectionId.LifeAreas}
      title={t('calendar.view-options.life-areas')}
      open={!collapsed}
      onToggle={onToggleSection}
      enabledCount={enabledCount}
      totalCount={totalCount}
      isFirst={isFirst}
    >
      {lifeAreas.map((lifeArea) => {
        const onToggle = () => toggleLifeArea(lifeArea.id);
        const show = !hiddenLifeAreaIds.includes(lifeArea.id);

        return (
          <SideNavigationPanelItem key={lifeArea.id}>
            <PopupMenuCheckbox
              label={lifeArea.name}
              checked={show}
              onChange={onToggle}
              color={lifeArea.color ?? undefined}
              dimmed={!show}
            />
          </SideNavigationPanelItem>
        );
      })}

      <SideNavigationPanelItem>
        <PopupMenuCheckbox
          label={t('general.no-life-area')}
          checked={showNoLifeArea}
          onChange={onToggleNoLifeArea}
          dimmed={!showNoLifeArea}
        />
      </SideNavigationPanelItem>
    </SideNavigationPanelSection>
  );
};
